import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DrawerService } from '../../../../../core/services/drawer.service';
import {
  BlockValueModel,
  DatasheetDataItem,
  DataSheetResult,
  EnterDataDrawerParams,
  FequencyTimeSlots,
  UnitMultiValue,
} from '../insights-data.interface';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { NzTableModule } from 'ng-zorro-antd/table';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { EnterDataHeadingComponent } from './enter-data-heading/enter-data-heading.component';
import {
  NzDatePickerComponent,
  NzDatePickerModule,
} from 'ng-zorro-antd/date-picker';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { FormsModule } from '@angular/forms';
import { FrequencyComponent } from './frequency/frequency.component';
import { CustomTimeService } from '../../../../manage-settings/custom-time/custom-time.service';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { LoaderService } from '../../../../../core/services/loader.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AllowOnlyValidNumbersDirective } from '../../../../../core/direactives/allowedOnlyValidNUmber.directive';
import { CommonService } from '../../../../../shared/services/common.service';
import { endOfWeek, isAfter, format, startOfDay, startOfWeek } from 'date-fns';
import {
  OrgScheduleList,
  TimeSlot,
} from '../../../../manage-settings/manage-settings.interface';
import { InsightsDataService } from '../insights-data.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { SetDurationComponent } from './set-duration/set-duration.component';
import { PartialIntervalComponent } from './partial-interval/partial-interval.component';
import _ from 'lodash';
import { ConfirmDialogComponent } from '../../../../../components/confirm-dialog/confirm-dialog.component';
import { UserDatePipe } from '../../../../../shared/pipes/user-date.pipe';
import { ManageDataNotesComponent } from './manage-data-notes/manage-data-notes.component';

@Component({
  selector: 'app-enter-data',
  standalone: true,
  imports: [
    EnterDataHeadingComponent,
    NzDatePickerModule,
    NzTableModule,
    CommonModule,
    NzButtonModule,
    NzInputModule,
    FormsModule,
    ConfirmDialogComponent,
    NzSpinModule,
    NgxMaskDirective,
    AllowOnlyValidNumbersDirective,
    UserDatePipe,
  ],
  providers: [
    provideNgxMask(), // Starts week on Sunday
  ],
  templateUrl: './enter-data.component.html',
  styleUrl: './enter-data.component.scss',
})
export class EnterDataComponent implements OnInit, OnDestroy {
  @ViewChild('weekPicker') weekPicker!: NzDatePickerComponent;
  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean> = of(false);
  drawerParams: EnterDataDrawerParams = {} as EnterDataDrawerParams;
  weekDays: { date: string; day: string }[] = [];
  pageNumber = 1;
  totalPages = 100;
  selectedRange: Date | null = null; // Changed back to selectedRange for week selection
  dataSheetDataItems: DatasheetDataItem[] = [] as DatasheetDataItem[];
  scheduleData: OrgScheduleList = {} as OrgScheduleList;
  isModify = false;
  lastDateOfCurrentWeek!: Date;
  behaviorData: DataSheetResult = {} as DataSheetResult;
  showConfirmDialog = false;
  isDeleting = false;
  deleteMsg = '';
  deleteObj: {
    id: number;
    date: string;
  } = {
    id: 0,
    date: '',
  };
  weekRange: Date[] = [];
  rangeErrorMsg = '';
  constructor(
    private drawerService: DrawerService,
    private customService: CustomTimeService,
    private loaderService: LoaderService,
    private commonService: CommonService,
    private insightsService: InsightsDataService,
    private cdr: ChangeDetectorRef
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
  }

  currentWeekStart!: string;

  ngOnInit() {
    // Set weekRange to [Sunday, Saturday] of the current week
    const today = new Date();
    const sunday = startOfWeek(today);
    const saturday = endOfWeek(today);
    this.weekRange = [sunday, saturday];
    this.onWeekRangeChange(this.weekRange);
    this.initializeWeeks();
    this.setLastDateOfCurrentWeek();
    this.getDataSheetData();
  }

  initializeWeeks() {
    const today = new Date(new Date().toISOString().split('T')[0]);
    const sunday = startOfWeek(today);
    const saturday = endOfWeek(today);
    this.weekDays = this.getCurrentWeekDates(sunday, saturday);

    this.currentWeekStart = this.weekDays[0].date; // Store the first day (Sunday) of the current week
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<EnterDataDrawerParams>(DRAWER_IDS.ENTER_DATA)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParams = params;
          this.getScheduleData();
        }
      });
  }
  setLastDateOfCurrentWeek() {
    const today = new Date();
    this.lastDateOfCurrentWeek = endOfWeek(today); // Get last day of current week (Saturday)
  }

  onChange(selectedDate: Date): void {
    if (!selectedDate) {
      this.selectedRange = null;
      return;
    }
    this.isModify = false;
    const sunday = startOfWeek(selectedDate);
    const saturday = endOfWeek(selectedDate);
    this.weekDays = this.getCurrentWeekDates(sunday, saturday);
    this.selectedRange = new Date(this.weekDays[0].date);
    this.getDataSheetData();
  }

  // Updated to only disable future dates (not weekends)
  disableFutureDates = (current: Date): boolean => {
    const today = startOfDay(new Date()); // Get today without time
    return isAfter(current, today); // Only disable future dates
  };

  getCurrentWeekDates(start: Date, end: Date): { date: string; day: string }[] {
    this.dataSheetDataItems = [];
    const weekDates = this.commonService.getRangeDates(start, end);
    weekDates.forEach(element => {
      const formattedDate = new Date(element.date).toISOString().split('T')[0];
      this.dataSheetDataItems.push({
        id: 0,
        date: formattedDate,
        day: element.day,
        individualIntervalDetails: '',
        totalFrequency: '',
        totalDuration: '',
        averageDuration: '',
        action: '',
        isFrequencyValue: false,
        isDuration: false,
        isUpdateTotal: false,
        isFrequencyOnly: false,
      });
    });
    this.selectedRange = new Date(weekDates[0].date);
    return weekDates;
  }

  get getGrandTotal() {
    return _.sumBy(
      this.dataSheetDataItems,
      item => Number(item.totalFrequency) || 0
    );
  }
  get getTotalOfDuration() {
    return _.sumBy(
      this.dataSheetDataItems,
      item => Number(item.totalDuration) || 0
    );
  }
  get getTotalforAverage() {
    return _.sumBy(
      this.dataSheetDataItems,
      item => Number(item.averageDuration) || 0
    );
  }

  get filteredDataSheetDataItems() {
    return this.dataSheetDataItems.filter(
      d => d.day !== 'Sunday' && d.day !== 'Saturday'
    );
  }

  getScheduleData() {
    if (this.drawerParams.caseDetail.student.customScheduleId) {
      this.loaderService.show();
      this.customService
        .getOrgSechduleById(
          this.drawerParams.caseDetail.student.customScheduleId
        )
        .pipe(
          take(1), // Take only one response
          finalize(() => this.loaderService.hide()), // End loading after response
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: items => {
            this.scheduleData = items[0];
          },
          error: (error: ErrorResponse) => {
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
          },
        });
    }
  }

  getDataSheetData() {
    this.loaderService.show();
    this.insightsService
      .getDataSheetData({
        CaseBehaviorId: this.drawerParams.caseBehaviorId,
        UnitName: this.drawerParams.selectedUnit,
        StartDate: this.weekDays[0].date,
        EndDate: this.weekDays[this.weekDays.length - 1].date, // Changed from 4 to 6 to include the full week
      })
      .pipe(
        take(1), // Take only one response
        finalize(() => this.loaderService.hide()), // End loading after response
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          if (response?.result) {
            this.behaviorData = response.result;
            this.cdr.detectChanges();

            this.dataSheetDataItems = this.dataSheetDataItems.map(weekDay => {
              let totalFrequency = '';
              let individualIntervalDetails = '';
              let isFrequencyValue = false;
              let isFrequencyOnly = false;
              let isDuration = false;
              let totalDuration = weekDay.totalDuration;
              let averageDuration = weekDay.averageDuration;
              let id = 0;

              this.cdr.detectChanges();

              const { activityDataUnitValues, activityDataUnitMultiValues } =
                this.behaviorData;

              // Handle activityDataUnitValues
              if (activityDataUnitValues.length > 0) {
                const matchedActivity = activityDataUnitValues.find(
                  activity => activity.dataDate === weekDay.date
                );
                if (matchedActivity) {
                  totalFrequency = matchedActivity.blockValue.toString();
                  id = matchedActivity.id;
                  isFrequencyValue = true;
                  isFrequencyOnly = true;
                }
              }

              if (activityDataUnitMultiValues.length > 0) {
                const matchedActivities = this.getFilteredActivities(
                  activityDataUnitMultiValues,
                  weekDay.date
                );
                if (matchedActivities.length > 0) {
                  isFrequencyValue = true;
                  totalFrequency =
                    this.getTotalFrequencyValue(matchedActivities);
                  individualIntervalDetails = this.getIntervalDetails(
                    matchedActivities,
                    weekDay
                  );
                }

                const matchedDurations = this.getFilteredDurations(
                  activityDataUnitMultiValues,
                  weekDay.date
                );
                if (matchedDurations.length > 0) {
                  isDuration = true;
                  totalFrequency =
                    this.getTotalFrequency(matchedDurations).toString();
                  totalDuration =
                    this.getTotalDuration(matchedDurations).toString();
                  averageDuration =
                    this.getAvgDuration(matchedDurations).toString();
                  individualIntervalDetails =
                    this.getDurationIntervals(matchedDurations).toString();
                }

                const matchedPartialInterval = this.getFilteredPartialIntervals(
                  activityDataUnitMultiValues,
                  weekDay.date
                );
                if (matchedPartialInterval.length > 0) {
                  isFrequencyValue = true;
                  individualIntervalDetails = this.getPartialIntervalDetails(
                    matchedPartialInterval
                  );
                }
              }

              return {
                ...weekDay,
                totalFrequency,
                individualIntervalDetails,
                isFrequencyValue,
                isFrequencyOnly,
                totalDuration,
                averageDuration,
                isDuration,
                id,
              };
            });

            this.cdr.detectChanges();
          }
        },
        error: (error: ErrorResponse) => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  // Helper functions
  getFilteredActivities(data: UnitMultiValue[], date: string) {
    return data.filter(
      activity =>
        activity.dataDate === date &&
        !activity.isDurationValue &&
        !activity.isIntervalFrequency
    );
  }

  getTotalFrequencyValue(matchedActivities: UnitMultiValue[]) {
    return _.sumBy(matchedActivities, activity =>
      Number(activity.unitValue)
    ).toString();
  }

  getIntervalDetails(
    matchedActivities: UnitMultiValue[],
    weekDay: DatasheetDataItem
  ) {
    const timeSlots = this.insightsService.generateTimeSlots(matchedActivities);
    return this.drawerParams.selectedUnit === 1 ||
      this.drawerParams.selectedUnit === 4
      ? this.getIndividualIntervalDetails(matchedActivities, weekDay, timeSlots)
      : matchedActivities.map(activity => activity.unitValue).join(', ');
  }

  getFilteredDurations(data: UnitMultiValue[], date: string) {
    return data.filter(
      activity =>
        activity.dataDate === date &&
        activity.isDurationValue &&
        !activity.isIntervalFrequency
    );
  }

  getFilteredPartialIntervals(data: UnitMultiValue[], date: string) {
    return data.filter(
      activity =>
        activity.dataDate === date &&
        !activity.isDurationValue &&
        activity.isIntervalFrequency
    );
  }

  //getInterval String
  getIndividualIntervalDetails(
    matchedActivities: UnitMultiValue[],
    weekDay: DatasheetDataItem,
    timeSlots: TimeSlot[] | FequencyTimeSlots[]
  ) {
    const unitArr: string[] = timeSlots.map(slot => {
      // Ensure start and end times match the expected format
      const formattedStartTime = `${slot.startTime}:00`;
      const formattedEndTime = `${slot.endTime}:00`;

      // Find a matching activity
      const matchedActivity = matchedActivities.find(
        ele =>
          ele.startDataTime === formattedStartTime &&
          ele.endDataTime === formattedEndTime &&
          ele.dataDate === weekDay.date
      );

      // If a match is found, return unitValue; otherwise, return empty string
      return matchedActivity
        ? matchedActivity.unitValue !== ''
          ? matchedActivity.unitValue
          : '-'
        : '-';
    });

    return unitArr.join(',');
  }

  getDurationIntervals(matchedDurations: UnitMultiValue[]) {
    return _.chain(matchedDurations)
      .map(item => {
        // Convert unitValue into an array of numbers (ignoring empty values)
        const sum = _.chain(item.unitValue)
          .split(',') // Split by commas
          .map(_.trim) // Trim spaces
          .filter(val => val !== '') // Remove empty values
          .map(_.toNumber) // Convert to numbers
          .filter(_.isFinite) // Remove NaN values
          .sum() // Sum all numbers
          .value();

        return sum === 0 ? '-' : sum; // Replace 0 with '-'
      })
      .join(',') // Join all sums with commas
      .value();
  }

  getPartialIntervalDetails(matchedActivities: UnitMultiValue[]) {
    return _.chain(matchedActivities)
      .map('unitValue') // Extract all unitValue strings
      .map(val => _.split(val, ',')) // Split each unitValue by comma
      .flatten() // Flatten nested arrays into a single array
      .map(val => (val.trim() !== '' ? val : '-')) // Remove empty values
      .join(',') // Join back into a comma-separated string
      .value();
  }
  //get Avg PT
  getAvgPartialInterval(data: string) {
    // Split the string and convert to numbers, treating empty values as null or undefined
    const unitValueArray = _.chain(data)
      .split(',') // Split string into an array by commas
      .map(val => _.trim(val)) // Trim spaces around values
      .filter(val => val !== '-') // Remove blank values
      .map(_.toNumber) // Convert remaining values to numbers
      .value();

    const avg = _.sum(unitValueArray) / unitValueArray.length;

    // Apply rounding rule
    return isNaN(avg) || avg === 0
      ? ''
      : avg % 1 === 0
        ? avg
        : Math.floor(avg) + (avg % 1 > 0.5 ? 1 : 0);
  }

  // get TOtal duration
  getTotalDuration(data: UnitMultiValue[]) {
    return _.chain(data)
      .map('unitValue') // Extract all unitValue strings
      .map(val => _.split(val, ',')) // Split each unitValue by comma
      .flatten() // Flatten nested arrays into a single array
      .map(val => _.toNumber(val)) // Convert to numbers (empty strings become NaN)
      .filter(val => !_.isNaN(val)) // Remove NaN values
      .sum() // Get the sum of all values
      .value();
  }

  //get avg duration
  getAvgDuration(data: UnitMultiValue[]) {
    // Extract number of non-blank values from the unitValue string
    const nonBlankUnitValues = _.flatMap(
      data,
      item =>
        item.unitValue
          .split(',') // Split the string by commas
          .map(value => value.trim()) // Trim spaces around values
          .filter(value => value !== '') // Filter out empty values
    ).length;
    const sumOfNonBlankUnitValues = _.sum(
      _.flatMap(
        data,
        item =>
          item.unitValue
            .split(',') // Split the string by commas
            .map(value => value.trim()) // Remove extra spaces around values
            .filter(value => value !== '') // Filter out any empty or blank values
            .map(value => _.toNumber(value)) // Convert the non-empty values to numbers
      )
    );
    const avgUnitValue = sumOfNonBlankUnitValues / nonBlankUnitValues;

    return isNaN(avgUnitValue) || avgUnitValue === 0
      ? ''
      : avgUnitValue % 1 === 0
        ? avgUnitValue
        : Math.floor(avgUnitValue) + (avgUnitValue % 1 > 0.5 ? 1 : 0);
  }

  getTotalFrequency(data: UnitMultiValue[]) {
    return _.flatMap(
      data,
      item =>
        item.unitValue
          .split(',') // Split the string by commas
          .map(value => value.trim()) // Trim any surrounding spaces
          .filter(value => value !== '') // Filter out empty values
    ).length;
  }

  isTotalFrequencyEdit(data: DatasheetDataItem) {
    return (
      this.isModify && this.isFutureDate(data.date) && data.isFrequencyValue
    );
  }
  openSetFrequencyDrawer(data: DatasheetDataItem): void {
    this.drawerService
      .openDrawer(
        FrequencyComponent,
        'Data',
        1170,
        true,
        undefined,
        {
          caseDetail: this.drawerParams.caseDetail,
          selectedUnit: this.drawerParams.selectedUnit,
          selectedDate: data.date,
          selectedDay: data.day,
          scheduleData: this.scheduleData,
          unitId: this.behaviorData.id,
          behaviorData: this.behaviorData,
          selectedRow: data,
          caseBehaviorId: this.drawerParams.caseBehaviorId,
        },
        DRAWER_IDS.FREQUENCY
      )
      .afterClose.subscribe(() => {
        this.getDataSheetData();
      });
  }

  openSetDurationDrawer(data: DatasheetDataItem) {
    this.drawerService
      .openDrawer(
        SetDurationComponent,
        'Data',
        1170,
        true,
        undefined,
        {
          caseDetail: this.drawerParams.caseDetail,
          selectedUnit: this.drawerParams.selectedUnit,
          selectedDate: data.date,
          selectedDay: data.day,
          scheduleData: this.scheduleData,
          unitId: this.behaviorData.id,
          caseBehaviorId: this.drawerParams.caseBehaviorId,
          behaviorData: this.behaviorData,
          selectedRow: data,
        },
        DRAWER_IDS.DURATION
      )
      .afterClose.subscribe(() => {
        this.getDataSheetData();
      });
  }

  openEnterDataDrawer(data: DatasheetDataItem) {
    this.drawerService
      .openDrawer(
        PartialIntervalComponent,
        'Data',
        520,
        true,
        undefined,
        {
          caseDetail: this.drawerParams.caseDetail,
          selectedUnit: this.drawerParams.selectedUnit,
          selectedDate: data.date,
          selectedDay: data.day,
          scheduleData: this.scheduleData,
          unitId: this.behaviorData.id,
          caseBehaviorId: this.drawerParams.caseBehaviorId,
          behaviorData: this.behaviorData,
          selectedRow: data,
        },
        DRAWER_IDS.PARTIAL_INTERVAL
      )
      .afterClose.subscribe(() => {
        this.getDataSheetData();
      });
  }

  openDataNotesDrawer(data: DatasheetDataItem) {
    this.drawerService
      .openDrawer(
        ManageDataNotesComponent,
        'Notes',
        1170,
        true,
        undefined,
        {
          caseDetail: this.drawerParams.caseDetail,
          selectedUnit: this.drawerParams.selectedUnit,
          selectedDate: data.date,
          selectedDay: data.day,
          scheduleData: this.scheduleData,
          unitId: this.behaviorData.id,
          caseBehaviorId: this.drawerParams.caseBehaviorId,
          behaviorData: this.behaviorData,
          selectedRow: data,
        },
        DRAWER_IDS.DATA_NOTES
      )
      .afterClose.subscribe(() => {
        this.getDataSheetData();
      });
  }

  openDataDrawer(data: DatasheetDataItem) {
    if (this.drawerParams.selectedUnit === 2) {
      this.openSetDurationDrawer(data);
    } else if (this.drawerParams.selectedUnit === 4) {
      this.openSetFrequencyDrawer(data);
    } else {
      this.openEnterDataDrawer(data);
    }
  }

  // Method to check if a date is in the future
  isFutureDate(dateString: string): boolean {
    const currentDate = new Date();
    const dataDate = new Date(dateString);
    return dataDate > currentDate;
  }

  prevWeek() {
    const rangeLength = this.weekDays?.length > 0 ? this.weekDays.length : 7;
    const currentStartDate = new Date(this.weekDays[0].date);
    const prevEnd = new Date(currentStartDate);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevEnd.getDate() - rangeLength + 1);
    this.weekRange = [prevStart, prevEnd];
    this.weekDays = this.getCurrentWeekDates(prevStart, prevEnd);
    this.getDataSheetData();
  }

  nextWeek() {
    if (!this.isCurrentWeek()) {
      const rangeLength = this.weekDays?.length > 0 ? this.weekDays.length : 7;
      const currentEndDate = new Date(
        this.weekDays[this.weekDays.length - 1].date
      );
      const nextStart = new Date(currentEndDate);
      nextStart.setDate(nextStart.getDate() + 1);
      const nextEnd = new Date(nextStart);
      nextEnd.setDate(nextStart.getDate() + rangeLength - 1);
      this.weekRange = [nextStart, nextEnd];
      this.weekDays = this.getCurrentWeekDates(nextStart, nextEnd);
      this.getDataSheetData();
    }
  }

  isCurrentWeek(): boolean {
    return (
      this.weekDays.length > 0 &&
      this.weekDays.some(day => day.date === this.currentWeekStart)
    );
  }

  modify() {
    this.isModify = true;
  }
  applyModify() {
    // Replace '0' with an empty string in totalFrequency
    const updatedRecords = _.map(this.dataSheetDataItems, item =>
      _.assign({}, item, {
        totalFrequency: item.totalFrequency === '0' ? '' : item.totalFrequency,
      })
    );

    // Filter records where totalFrequency is NOT an empty string and isUpdateTotal is true
    const filteredRecords = _.filter(
      updatedRecords,
      item => item.totalFrequency !== '' && item.isUpdateTotal
    );
    // Map the filtered records into the desired structure
    const blockValues = filteredRecords.map(item => ({
      id: item.id,
      dataDate: item.date,
      unitValue: parseInt(item.totalFrequency, 10), // Assuming the value in `totalFrequency` is numeric
    }));

    const payload: BlockValueModel = {
      unitId: this.behaviorData.id,
      blockValues: blockValues,
    };
    this.saveBlockValue(payload);
  }
  cancelModify() {
    this.getDataSheetData();
    this.isModify = false;
  }

  get totalTitle(): string {
    return `Total ${this.drawerParams.selectedUnit === 2 ? 'Latency' : 'Duration'}`;
  }
  get avgTitle(): string {
    return `Average ${this.drawerParams.selectedUnit === 2 ? 'Latency' : 'Duration'}`;
  }
  saveBlockValue(payload: BlockValueModel) {
    this.loaderService.show();
    const operation$ = this.insightsService.saveBlockValue(payload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', `Datasheet saved successfully`);
          this.isModify = false;
          this.getDataSheetData();
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  getUnitValues(univalues: string[]): string {
    return univalues
      .map(row => row?.trim() || '') // Replace empty values with "N/A"
      .join(', '); // Join with a single comma
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  delete(data: DatasheetDataItem) {
    this.deleteMsg = `Are you sure you want to delete the data recorded for ${format(new Date(data.date), 'dd MMMM yyyy')}?`;
    this.showConfirmDialog = true;
    this.deleteObj = {
      id: this.behaviorData.id,
      date: data.date,
    };
    data.totalDuration = '';
    data.totalFrequency = '';
    data.averageDuration = '';
  }

  removeUnitValue() {
    if (this.behaviorData.id) {
      this.isDeleting = true;
      const operation$ = this.insightsService.deleteUnitValue(
        this.deleteObj.id,
        this.deleteObj.date
      );
      operation$
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          finalize(() => {
            this.isDeleting = false;
          })
        )
        .subscribe({
          next: () => {
            this.getDataSheetData();
            this.isDeleting = false;
            this.showConfirmDialog = false;
            this.commonService.notify(
              'success',
              'Record deleted successfully.'
            );
            this.cdr.detectChanges();
          },
          error: error => {
            this.isDeleting = false;
            this.showConfirmDialog = false;
            this.handleError(error);
          },
        });
    }
  }

  handleCancel() {
    this.showConfirmDialog = false;
    this.isDeleting = false;
  }

  onWeekRangeChange(range: Date[]) {
    if (!range || range.length !== 2) return;
    const diffInMs = range[1].getTime() - range[0].getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24) + 1; // inclusive
    if (diffInDays > 30) {
      this.rangeErrorMsg = 'You can select a maximum of 30 days.';
      return;
    } else {
      this.rangeErrorMsg = '';
    }
    this.rangeErrorMsg = '';
    this.weekDays = this.getCurrentWeekDates(range[0], range[1]);
    this.weekRange = range;
    this.getDataSheetData();
  }
}
